import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { VisitRow, useEstablishmentVisits, useEstablishmentFollowup } from "@/hooks/useClientFollowup";
import { supabase } from "@/integrations/supabase/client";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  establishmentId: string;
  visit?: VisitRow | null;
}

const empty = {
  visit_date: new Date().toISOString().slice(0, 10),
  start_time: "",
  end_time: "",
  is_full_day: false,
  location: "",
  visit_type: "commercial",
  status: "planned",
  summary: "",
  detailed_report: "",
  issues_detected: "",
  actions_to_do: "",
  needs_followup: false,
  next_action_at: "",
  next_action_status: "todo",
  client_health: "ok",
  health_reason: "",
  recommended_frequency: "quarterly",
  priority: "normal",
};

export function VisitDialog({ open, onOpenChange, establishmentId, visit }: Props) {
  const { save } = useEstablishmentVisits(establishmentId);
  const { upsert: upsertFollowup } = useEstablishmentFollowup(establishmentId);
  const [form, setForm] = useState<any>(empty);

  useEffect(() => {
    if (visit) {
      setForm({
        ...empty,
        ...visit,
        start_time: visit.start_time ?? "",
        end_time: visit.end_time ?? "",
        location: visit.location ?? "",
        summary: visit.summary ?? "",
        detailed_report: visit.detailed_report ?? "",
        issues_detected: visit.issues_detected ?? "",
        actions_to_do: visit.actions_to_do ?? "",
        next_action_at: visit.next_action_at ? visit.next_action_at.slice(0, 16) : "",
        next_action_status: visit.next_action_status ?? "todo",
        client_health: visit.client_health ?? "ok",
        health_reason: visit.health_reason ?? "",
        recommended_frequency: visit.recommended_frequency ?? "quarterly",
      });
    } else {
      setForm(empty);
    }
  }, [visit, open]);

  const set = (k: string, v: any) => setForm((p: any) => ({ ...p, [k]: v }));

  const handleSubmit = async () => {
    const payload: any = {
      ...(visit?.id ? { id: visit.id } : {}),
      visit_date: form.visit_date,
      start_time: form.is_full_day || !form.start_time ? null : form.start_time,
      end_time: form.is_full_day || !form.end_time ? null : form.end_time,
      is_full_day: form.is_full_day,
      location: form.location || null,
      visit_type: form.visit_type,
      status: form.status,
      summary: form.summary || null,
      detailed_report: form.detailed_report || null,
      issues_detected: form.issues_detected || null,
      actions_to_do: form.actions_to_do || null,
      needs_followup: form.needs_followup,
      next_action_at: form.next_action_at ? new Date(form.next_action_at).toISOString() : null,
      next_action_status: form.needs_followup ? form.next_action_status : null,
      client_health: form.client_health || null,
      health_reason: form.health_reason || null,
      recommended_frequency: form.recommended_frequency || null,
      priority: form.priority,
    };
    await save.mutateAsync(payload);

    // Propagate health diagnostic to establishment_followup (source of truth at establishment level)
    if (form.client_health || form.priority || form.recommended_frequency || form.health_reason) {
      try {
        const { data: u } = await supabase.auth.getUser();
        await upsertFollowup.mutateAsync({
          client_health: form.client_health || null,
          priority: form.priority || null,
          recommended_frequency: form.recommended_frequency || null,
          health_reason: form.health_reason || null,
          health_updated_at: new Date().toISOString(),
          health_updated_by: u.user?.id ?? null,
        } as any);
      } catch (_e) {
        // non-blocking
      }
    }

    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{visit ? "Modifier la visite" : "Nouvelle visite"}</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Date</Label>
            <Input type="date" value={form.visit_date} onChange={(e) => set("visit_date", e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Statut</Label>
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="planned">Planifiée</SelectItem>
                <SelectItem value="done">Effectuée</SelectItem>
                <SelectItem value="cancelled">Annulée</SelectItem>
                <SelectItem value="to_reschedule">À replanifier</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="col-span-2 flex items-center gap-2">
            <Switch checked={form.is_full_day} onCheckedChange={(v) => set("is_full_day", v)} />
            <Label>Journée entière</Label>
          </div>

          {!form.is_full_day && (
            <>
              <div className="space-y-2">
                <Label>Heure début</Label>
                <Input type="time" value={form.start_time} onChange={(e) => set("start_time", e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Heure fin</Label>
                <Input type="time" value={form.end_time} onChange={(e) => set("end_time", e.target.value)} />
              </div>
            </>
          )}

          <div className="col-span-2 space-y-2">
            <Label>Lieu de rendez-vous</Label>
            <Input value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="Sur place / Visioconférence / ..." />
          </div>

          <div className="space-y-2">
            <Label>Type de visite</Label>
            <Select value={form.visit_type} onValueChange={(v) => set("visit_type", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="technical_audit">Audit technique</SelectItem>
                <SelectItem value="commercial">Commerciale</SelectItem>
                <SelectItem value="training">Formation</SelectItem>
                <SelectItem value="client_relation">Relation client</SelectItem>
                <SelectItem value="urgent_issue">Problème urgent</SelectItem>
                <SelectItem value="field_followup">Suivi terrain</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Priorité de suivi</Label>
            <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="high">Haute</SelectItem>
                <SelectItem value="normal">Normale</SelectItem>
                <SelectItem value="low">Basse</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="col-span-2 space-y-2">
            <Label>Résumé court</Label>
            <Input value={form.summary} onChange={(e) => set("summary", e.target.value)} />
          </div>
          <div className="col-span-2 space-y-2">
            <Label>Compte-rendu détaillé</Label>
            <Textarea rows={4} value={form.detailed_report} onChange={(e) => set("detailed_report", e.target.value)} />
          </div>
          <div className="col-span-2 space-y-2">
            <Label>Problèmes détectés</Label>
            <Textarea rows={2} value={form.issues_detected} onChange={(e) => set("issues_detected", e.target.value)} />
          </div>
          <div className="col-span-2 space-y-2">
            <Label>Actions à faire</Label>
            <Textarea rows={2} value={form.actions_to_do} onChange={(e) => set("actions_to_do", e.target.value)} />
          </div>

          <div className="col-span-2 flex items-center gap-2 pt-2 border-t">
            <Switch checked={form.needs_followup} onCheckedChange={(v) => set("needs_followup", v)} />
            <Label>Suivi nécessaire</Label>
          </div>
          {form.needs_followup && (
            <>
              <div className="space-y-2">
                <Label>Prochaine action (date + heure)</Label>
                <Input type="datetime-local" value={form.next_action_at} onChange={(e) => set("next_action_at", e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Statut de l'action</Label>
                <Select value={form.next_action_status} onValueChange={(v) => set("next_action_status", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todo">À faire</SelectItem>
                    <SelectItem value="in_progress">En cours</SelectItem>
                    <SelectItem value="done">Fait</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          <div className="space-y-2 pt-2 border-t col-span-2 grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Santé client</Label>
              <Select value={form.client_health} onValueChange={(v) => set("client_health", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ok">OK</SelectItem>
                  <SelectItem value="watch">À surveiller</SelectItem>
                  <SelectItem value="risk">Risque</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Fréquence recommandée</Label>
              <Select value={form.recommended_frequency} onValueChange={(v) => set("recommended_frequency", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Mensuelle</SelectItem>
                  <SelectItem value="quarterly">Trimestrielle</SelectItem>
                  <SelectItem value="biannual">Semestrielle</SelectItem>
                  <SelectItem value="annual">Annuelle</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="col-span-2 space-y-2">
            <Label>Raison de l'évaluation santé</Label>
            <Textarea rows={2} value={form.health_reason} onChange={(e) => set("health_reason", e.target.value)} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={handleSubmit} disabled={save.isPending || !form.visit_date}>
            {save.isPending ? "Enregistrement..." : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
